import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { makeRequest } from 'Douleutaras/services/apiClient';
import { RootState } from 'Douleutaras/redux/store';
import { mapServicePriceSettings } from 'Douleutaras/views/seller-dashboard/settings/Profile/EditServices/servicePriceSettingsMapper';
import {
  SettingsServicePricesDocument,
  SettingsServicesCategory
} from 'Douleutaras/views/seller-dashboard/settings/Profile/EditServices/types';

// Reads the pro from the JWT; prices, catalog items and level labels are included by default
export const SERVICE_PRICE_SETTINGS_ENDPOINT = 'api/public/v3/user/settings/service-prices/';

export const getServicePriceSettings = createAsyncThunk<Array<SettingsServicesCategory>, void, { state: RootState }>(
  'settings/servicePrices/get',
  async (_, api) => {
    const response = await makeRequest(SERVICE_PRICE_SETTINGS_ENDPOINT, { method: 'GET' })(api);

    const doc = response?.data as SettingsServicePricesDocument | undefined;
    if (!doc || !Array.isArray(doc.data)) {
      throw new Error('Malformed service-price settings response');
    }

    return mapServicePriceSettings(doc);
  }
);

export interface ServicePriceSettingsState {
  categories: Array<SettingsServicesCategory>;
  loading: boolean;
  error: boolean;
  // Set once a list has arrived, so a refetch keeps it on screen
  loaded: boolean;
}

const initialState: ServicePriceSettingsState = {
  categories: [],
  loading: false,
  error: false,
  loaded: false
};

export const servicePriceSettingsSlice = createSlice({
  name: 'servicePriceSettings',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(getServicePriceSettings.pending, state => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(getServicePriceSettings.fulfilled, (state, action) => {
      state.categories = action.payload;
      state.loading = false;
      state.loaded = true;
    });
    builder.addCase(getServicePriceSettings.rejected, state => {
      state.loading = false;
      state.error = true;
    });
  }
});

export const selectServicePriceSettings = (state: RootState): ServicePriceSettingsState => state.servicePriceSettings;

export default servicePriceSettingsSlice.reducer;
